import { cn, formatFileSize } from "@/lib/utils";
import { MessageContentEntity } from "@/indexedDB/types";
import MediaAttachment from "./MediaAttachment";
import FileAttachment from "./FileAttachment";
import { TruncatedFilename } from "@/components/primitives/TruncatedFilename";
import { useAttachmentViewerState } from "@/zustand/AttachmentViewerState";
import VoiceAttachment from "./VoiceAttachment";
import useFileDownload from "@/middleware/hooks/attachments/useFileDownload";
import useAutoDownload from "@/middleware/hooks/attachments/useAutoDownload";
import { PDF_PREVIEW_MAX_BYTES } from "@/config/constants";
import { useDownloadProgressState } from "@/zustand/DownloadProgressState";
import { useTranslation } from "react-i18next";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  messageId: string;
  messageTimestamp: string;
  chatId: string;
  messageContent: MessageContentEntity;
  isOutgoingMessage: boolean;
  /**
   * Context this message belongs to — the parent message id for a thread
   * comment, or null for a main-chat message. Scopes the viewer's media
   * navigation so thread media and main-chat media stay separate.
   */
  threadId?: string | null;
}

// TO DO => consider improving the abstraction of this component
// renaming this to "MessageAttachmentRenderer" since it can render different types of attachments
// or consider breaking down into separate components for each attachment type
// and then this component just becomes a wrapper that decides what  to render based on the contentt
// this would simplify the logic and make it more maintainable as we add more attachment types in the future

export default function MessageAttachment({
  messageId,
  chatId,
  messageContent,
  messageTimestamp,
  isOutgoingMessage,
  threadId = null,
}: Props) {
  const { t } = useTranslation();
  const openDialog = useAttachmentViewerState((state) => state.openDialog);

  const attachment = messageContent.media
    ? messageContent.media!
    : messageContent.file!;

  const isPdf = attachment?.fileType === "application/pdf";

  // Large PDFs take the download route rather than the in-app viewer: pdf.js is
  // handed a `blob:` URL, which cannot serve range requests, so it must buffer
  // the whole document before painting page 1 — on top of a whole-file decrypt.
  // A missing fileSize (older messages) stays previewable, preserving today's
  // behaviour rather than silently demoting existing attachments.
  const isPreviewablePdf =
    isPdf && (attachment?.fileSize ?? 0) <= PDF_PREVIEW_MAX_BYTES;

  // Byte progress is published by the single-flight download keyed on the
  // attachment id (see processFileAttachment), so subscribe by id rather than
  // expecting a callback the dedupe would drop for late joiners.
  //
  // Keyed on the FILE id specifically, not `attachment` (which is media when
  // present). Only the document card consumes this, while the media path
  // publishes progress on every viewer open, prefetch and auto-download — so
  // subscribing to a media id would re-render this component (and the
  // unmemoised MediaAttachment below it) on every XHR tick for a value nothing
  // reads.
  const downloadProgress = useDownloadProgressState(
    (s) => s.progress[messageContent.file?.attachmentId ?? ""],
  );

  const { status: downloadStatus, download } = useFileDownload({
    chatId,
    messageId,
    timestamp: messageTimestamp,
    attachment: messageContent.file,
  });

  // Opt-in auto-download for documents (default off → no-op). Media auto-download
  // is handled inside MediaAttachment; this covers the file case only.
  useAutoDownload({
    chatId,
    messageId,
    timestamp: messageTimestamp,
    attachment: messageContent.file,
    kind: "file",
  });

  // Documents that can't be previewed in-app download straight to disk instead
  // of opening the viewer to a dead end: every non-PDF document, plus PDFs over
  // the preview ceiling. Images/videos and small-enough PDFs open the viewer.
  // The card's glyph and aria-label read from this same value, so what the card
  // advertises cannot drift from what the click does.
  const willDownloadOnClick = Boolean(messageContent.file) && !isPreviewablePdf;

  function handleClick() {
    if (willDownloadOnClick) {
      download();
      return;
    }
    openDialog(
      {
        chatId,
        messageId,
        timestamp: messageTimestamp,
        id: attachment.attachmentId,
        filename: attachment.filename,
        fileType: attachment.fileType,
        isLargeAttachment: attachment.isLargeAttachment,
        // Native dims so the viewer frame is correct on the first paint.
        width: messageContent.media?.width,
        height: messageContent.media?.height,
      },
      {
        threadId,
        uploadState: messageContent.media?.uploadState,
        fileSize: attachment.fileSize,
      },
    );
  }

  const element = messageContent.media ? (
    // min-w-0 lets the media bubble shrink below its definite width so it tracks
    // the column on mobile (see MediaAttachment's fill-width sizing).
    <div className="min-w-0 cursor-pointer select-none" onClick={handleClick}>
      <MediaAttachment
        messageId={messageId}
        messageTimestamp={messageTimestamp}
        chatId={chatId}
        attachment={messageContent.media}
        isOutgoing={isOutgoingMessage}
      />
    </div>
  ) : messageContent.file ? (
    // Horizontal card: [icon] [filename / size]. The whole card is the click
    // target (download for non-PDF, viewer for PDF).
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      // Non-visual parity with the glyph: announce that this one saves to disk
      // rather than opening. Previewable PDFs keep the default announcement
      // (filename + size from the card's own content).
      aria-label={
        willDownloadOnClick
          ? `${t("download")} ${messageContent.file.filename}`
          : undefined
      }
      onKeyDown={(e) => {
        // Keyboard parity with the click target (download when the card shows
        // the download glyph, viewer otherwise): Enter/Space activate it, like a
        // native button.
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        // max-w-full, NOT w-fit: `width: fit-content` resolves against the
        // widest thing inside, so a long filename sized the card past the
        // bubble and the bubble's overflow-hidden clipped it (issue #1131).
        // Filling the available width is safe — the bubble itself still hugs
        // its content, so short filenames get a short card.
        "flex max-w-full cursor-pointer select-none items-center gap-3 rounded-md p-2",
        isOutgoingMessage
          ? "bg-primary/20 dark:bg-primary dark:text-primary-foreground"
          : "bg-accent text-accent-foreground",
        messageContent.media
          ? `rounded-sm border-4 ${isOutgoingMessage ? "border-primary" : "border-accent"}`
          : "",
      )}
    >
      <FileAttachment
        downloadStatus={downloadStatus}
        willDownload={willDownloadOnClick}
        progress={downloadProgress}
      />
      {/* text-left: an outgoing bubble sets text-right, which pushed the size
          line to the far edge of the card. max-w-xs keeps the card bounded on
          desktop; on mobile the column shrinks and the filename ellipsizes. */}
      <div className="flex min-w-0 max-w-xs flex-col text-left">
        <TruncatedFilename filename={messageContent.file.filename} />
        <div className="text-xs opacity-30">
          {formatFileSize(messageContent.file.fileSize)}
        </div>
      </div>
    </div>
  ) : messageContent.voice ? (
    <VoiceAttachment
      messageId={messageId}
      messageTimestamp={messageTimestamp}
      chatId={chatId}
      attachmentMeta={messageContent.voice}
      isOutgoingMessage={isOutgoingMessage}
    />
  ) : (
    <></>
  );

  return <>{element}</>;
}
